import { useEffect } from 'react';
import { useLanguage } from '../i18n/LanguageContext';

const SEOHead = () => {
  const { t, language } = useLanguage();

  useEffect(() => {
    // Update document title
    document.title = t('metaTitle');

    // Update meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = t('metaDescription');

    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = t('metaKeywords');

    // Update Open Graph tags
    updateMetaTag('og:title', t('metaTitle'));
    updateMetaTag('og:description', t('metaDescription'));
    updateMetaTag('og:type', 'website');
    updateMetaTag('og:locale', language === 'fr' ? 'fr_FR' : language === 'de' ? 'de_DE' : 'en_US');

    // Update Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', t('metaTitle'));
    updateMetaTag('twitter:description', t('metaDescription'));

    // Update language
    document.documentElement.lang = language;

    // Add hreflang tags for multilingual SEO
    updateHreflang();

    // Add JSON-LD structured data for LLM optimization
    updateStructuredData();

  }, [t, language]);

  const updateMetaTag = (property, content) => {
    let tag = document.querySelector(`meta[property="${property}"]`) || 
              document.querySelector(`meta[name="${property}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      if (property.startsWith('og:') || property.startsWith('twitter:')) {
        tag.setAttribute('property', property);
      } else {
        tag.name = property;
      }
      document.head.appendChild(tag);
    }
    tag.content = content;
  };

  const updateHreflang = () => {
    // Remove existing hreflang links
    document.querySelectorAll('link[hreflang]').forEach(el => el.remove());

    const baseUrl = window.location.origin;
    const languages = ['en', 'fr', 'de'];

    languages.forEach(lang => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = lang;
      link.href = `${baseUrl}?lang=${lang}`;
      document.head.appendChild(link);
    });

    // Add x-default
    const defaultLink = document.createElement('link');
    defaultLink.rel = 'alternate';
    defaultLink.hreflang = 'x-default';
    defaultLink.href = baseUrl;
    document.head.appendChild(defaultLink);
  };

  const updateStructuredData = () => {
    // Remove existing JSON-LD
    document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());

    // Game structured data for LLM and search engines
    const gameSchema = {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      "name": "Ultimate Texas Hold'em",
      "description": t('metaDescription'),
      "genre": ["Casino", "Poker", "Card Game"],
      "gamePlatform": ["Web Browser"],
      "applicationCategory": "Game",
      "operatingSystem": "Any",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "15420",
        "bestRating": "5",
        "worstRating": "1"
      },
      "author": {
        "@type": "Organization",
        "name": "Vegas Casino Games"
      },
      "inLanguage": [language, "en", "fr", "de"],
      "isAccessibleForFree": true,
      "keywords": t('metaKeywords')
    };

    // FAQ Schema for better SEO
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": language === 'fr' ? "Comment jouer à Ultimate Texas Hold'em ?" : 
                  language === 'de' ? "Wie spielt man Ultimate Texas Hold'em?" :
                  "How to play Ultimate Texas Hold'em?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": language === 'fr' ? "Placez vos mises Ante et Blind, recevez 2 cartes, puis décidez de miser 3x/4x, checker, ou vous coucher au fur et à mesure que les cartes communes sont révélées." :
                    language === 'de' ? "Setzen Sie Ihre Ante- und Blind-Einsätze, erhalten Sie 2 Karten, dann entscheiden Sie sich für 3x/4x setzen, checken oder passen, während die Gemeinschaftskarten aufgedeckt werden." :
                    "Place your Ante and Blind bets, receive 2 cards, then decide to bet 3x/4x, check, or fold as community cards are revealed."
          }
        },
        {
          "@type": "Question",
          "name": language === 'fr' ? "L'Ultimate Texas Hold'em est-il gratuit ?" :
                  language === 'de' ? "Ist Ultimate Texas Hold'em kostenlos?" :
                  "Is Ultimate Texas Hold'em free?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": language === 'fr' ? "Oui, notre jeu est 100% gratuit avec des jetons virtuels. Aucun argent réel requis." :
                    language === 'de' ? "Ja, unser Spiel ist 100% kostenlos mit virtuellen Chips. Kein echtes Geld erforderlich." :
                    "Yes, our game is 100% free with virtual chips. No real money required."
          }
        },
        {
          "@type": "Question",
          "name": language === 'fr' ? "Quelle est la meilleure stratégie ?" :
                  language === 'de' ? "Was ist die beste Strategie?" :
                  "What is the best strategy?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": language === 'fr' ? "Misez 4x avec des paires fortes et des As assortis. Misez 3x avec des mains moyennes. Checkez les mains faibles et misez 1x seulement avec des mains faites au river." :
                    language === 'de' ? "Setzen Sie 4x bei starken Paaren und suited Assen. Setzen Sie 3x bei mittleren Händen. Checken Sie schwache Hände und setzen Sie 1x nur mit gemachten Händen am River." :
                    "Bet 4x with strong pairs and suited aces. Bet 3x with medium hands. Check weak hands and bet 1x only with made hands at river."
          }
        }
      ]
    };

    // WebApplication Schema
    const webAppSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Ultimate Texas Hold'em Online",
      "url": window.location.href,
      "applicationCategory": "GameApplication",
      "browserRequirements": "Requires JavaScript",
      "softwareVersion": "1.0",
      "operatingSystem": "All",
      "offers": {
        "@type": "Offer",
        "price": "0"
      },
      "featureList": [
        "Free to play",
        "Virtual chips",
        "Strategy advice",
        "Multilingual (EN/FR/DE)",
        "No download required",
        "Mobile friendly"
      ]
    };

    // Add all schemas
    [gameSchema, faqSchema, webAppSchema].forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  };

  return null; // This component only manages head tags
};

export default SEOHead;
